import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';

/**
 * Free-text autocomplete for the "Master Data Table" cell.
 *
 * Replaces the native HTML <datalist>, whose popup/caret are browser-rendered
 * and cannot be styled, with an app-styled listbox. It stays a real text
 * input (not a DS Select) so any value —
 * including legacy/unlisted table names — can still be typed; the listbox only
 * offers suggestions filtered by what's typed.
 */

// Stable id seed (React 17 has no useId).
let __comboSeq = 0;

export default function MasterDataTableCombobox({
	value,
	onChange,
	options = [],
	disabled = false,
	placeholder,
}) {
	const [open, setOpen] = useState(false);
	const [activeIndex, setActiveIndex] = useState(0);
	const rootRef = useRef(null);

	const idRef = useRef(null);
	if (idRef.current === null) {
		__comboSeq += 1;
		idRef.current = `cv-mdm-combo-${__comboSeq}`;
	}
	const listId = `${idRef.current}-list`;
	const optionId = (index) => `${idRef.current}-opt-${index}`;

	const query = (value || '').trim().toLowerCase();
	const matchesExisting = options.some((opt) => opt.toLowerCase() === query);
	// Show everything when the field is empty or already holds an exact option
	// (so re-focusing lets the user switch); otherwise filter by substring.
	const filtered = (!query || matchesExisting)
		? options
		: options.filter((opt) => opt.toLowerCase().includes(query));

	const canOpen = !disabled && options.length > 0;
	const showList = open && canOpen && filtered.length > 0;

	// Close on outside mousedown while open.
	useEffect(() => {
		if (!open) return undefined;
		const handleMouseDown = (event) => {
			if (rootRef.current && !rootRef.current.contains(event.target)) {
				setOpen(false);
			}
		};
		document.addEventListener('mousedown', handleMouseDown);
		return () => document.removeEventListener('mousedown', handleMouseDown);
	}, [open]);

	const selectOption = (opt) => {
		onChange(opt);
		setOpen(false);
	};

	const handleInputChange = (event) => {
		onChange(event.target.value);
		setActiveIndex(0);
		if (canOpen) setOpen(true);
	};

	const handleKeyDown = (event) => {
		if (disabled) return;
		const { key } = event;
		if (!open) {
			if (canOpen && (key === 'ArrowDown' || key === 'ArrowUp')) {
				event.preventDefault();
				setActiveIndex(0);
				setOpen(true);
			}
			return;
		}
		if (key === 'ArrowDown') {
			event.preventDefault();
			setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
		} else if (key === 'ArrowUp') {
			event.preventDefault();
			setActiveIndex((i) => Math.max(i - 1, 0));
		} else if (key === 'Enter') {
			if (filtered[activeIndex] !== undefined) {
				event.preventDefault();
				selectOption(filtered[activeIndex]);
			}
		} else if (key === 'Escape') {
			event.preventDefault();
			setOpen(false);
		}
	};

	return (
		<div className="dsv-cv-combo" ref={rootRef}>
			<input
				className="dsv-modal__input dsv-cv-combo__input"
				type="text"
				role="combobox"
				aria-expanded={showList}
				aria-controls={listId}
				aria-autocomplete="list"
				aria-activedescendant={showList ? optionId(activeIndex) : undefined}
				autoComplete="off"
				value={value}
				disabled={disabled}
				placeholder={placeholder}
				data-testid="cv-mdm-table-input"
				onChange={handleInputChange}
				onFocus={() => { if (canOpen) setOpen(true); }}
				onKeyDown={handleKeyDown}
			/>

			{!disabled && options.length > 0 && (
				<button
					type="button"
					className={classNames('dsv-cv-combo__chevron', { 'dsv-cv-combo__chevron--open': open })}
					tabIndex={-1}
					aria-label={open ? 'Hide suggestions' : 'Show suggestions'}
					data-testid="cv-mdm-table-chevron"
					onClick={() => {
						setActiveIndex(0);
						setOpen((prev) => !prev);
					}}
				>
					<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
						<path d="M3 4.5 6 7.5 9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
					</svg>
				</button>
			)}

			{showList && (
				<ul
					className="dsv-cv-combo__listbox"
					id={listId}
					role="listbox"
					data-testid="cv-mdm-table-listbox"
				>
					{filtered.map((opt, i) => (
						<li
							key={opt}
							id={optionId(i)}
							role="option"
							aria-selected={opt === value}
							className={classNames('dsv-cv-combo__option', {
								'dsv-cv-combo__option--active': i === activeIndex,
								'dsv-cv-combo__option--selected': opt === value,
							})}
							data-testid="cv-mdm-table-option"
							onMouseEnter={() => setActiveIndex(i)}
							// mousedown (not click) so selection beats the input's blur.
							onMouseDown={(event) => { event.preventDefault(); selectOption(opt); }}
						>
							{opt}
						</li>
					))}
				</ul>
			)}
		</div>
	);
}

MasterDataTableCombobox.propTypes = {
	value: PropTypes.string,
	onChange: PropTypes.func.isRequired,
	options: PropTypes.arrayOf(PropTypes.string),
	disabled: PropTypes.bool,
	placeholder: PropTypes.string,
};
